import { useEffect, useRef, useState, useCallback } from 'react';
import { useGuidedTourStore } from './store';

const STEP_CONFIG: Record<string, { text: string; preferredPlacement: 'top' | 'bottom' | 'left' | 'right'; arrow?: boolean }> = {
  'nav-study': { text: 'Отлично! У вас 100 слов → нажмите сюда', preferredPlacement: 'top', arrow: true },
  'select-mc': { text: 'Нажмите «Начать учёбу» — это карточки с выбором ответа', preferredPlacement: 'bottom', arrow: true },
  'mc-card':   { text: 'Выберите правильный перевод', preferredPlacement: 'top', arrow: true },
  'mc-result': { text: 'Правильно! Слово отправлено в повторение — вы увидите его снова через 8 часов', preferredPlacement: 'top', arrow: false },
  'finish':    { text: 'Отлично! Вы прошли первую тренировку. Новые слова будут появляться для изучения, а старые — для повторения.', preferredPlacement: 'top', arrow: false },
};

const TOOLTIP_HEIGHT_EST = 140;
const ARROW_SIZE = 8;

function computePlacement(
  preferred: 'top' | 'bottom' | 'left' | 'right',
  rect: DOMRect,
  tooltipW = 280,
  tooltipH = TOOLTIP_HEIGHT_EST
): 'top' | 'bottom' | 'left' | 'right' {
  const margin = 8;
  const fitsTop = rect.top - tooltipH - margin - ARROW_SIZE >= 0;
  const fitsBottom = rect.bottom + tooltipH + margin + ARROW_SIZE <= window.innerHeight;
  const fitsLeft = rect.left - tooltipW - margin - ARROW_SIZE >= 0;
  const fitsRight = rect.right + tooltipW + margin + ARROW_SIZE <= window.innerWidth;

  if (preferred === 'bottom' && fitsBottom) return 'bottom';
  if (preferred === 'top' && fitsTop) return 'top';
  if (fitsBottom) return 'bottom';
  if (fitsTop) return 'top';
  if (fitsLeft) return 'left';
  if (fitsRight) return 'right';
  return 'bottom';
}

export function GuidedTourOverlay() {
  const { active, step, nextStep, endTour } = useGuidedTourStore();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [found, setFound] = useState(false);
  const [tooltipH, setTooltipH] = useState(TOOLTIP_HEIGHT_EST);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevTargetRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const findTarget = useCallback(() => {
    if (!step || !active) return;
    const selector = `[data-tour-target="${step}"]`;
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) {
      if (prevTargetRef.current && prevTargetRef.current !== el) {
        prevTargetRef.current.style.boxShadow = '';
        prevTargetRef.current.style.position = '';
        prevTargetRef.current.style.zIndex = '';
      }
      prevTargetRef.current = el;
      const r = el.getBoundingClientRect();
      setRect(r);
      setFound(true);
      // highlight
      el.style.boxShadow = '0 0 0 4px rgba(0, 200, 255, 0.6), 0 0 20px rgba(0, 200, 255, 0.3)';
      el.style.position = 'relative';
      el.style.zIndex = '1000';
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setFound(false);
      setRect(null);
    }
  }, [step, active]);

  useEffect(() => {
    if (!active || !step) {
      if (prevTargetRef.current) {
        prevTargetRef.current.style.boxShadow = '';
        prevTargetRef.current.style.position = '';
        prevTargetRef.current.style.zIndex = '';
        prevTargetRef.current = null;
      }
      return;
    }
    findTarget();
    intervalRef.current = setInterval(findTarget, 500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, step, findTarget]);

  // Measure tooltip height after render to adjust placement
  useEffect(() => {
    if (tooltipRef.current) {
      setTooltipH(tooltipRef.current.offsetHeight);
    }
  }, [step, found]);

  if (!active || !step || !found || !rect) return null;

  const config = STEP_CONFIG[step];
  if (!config) return null;

  const placement = computePlacement(config.preferredPlacement, rect, 280, tooltipH);

  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10001,
    background: 'var(--theme-bg-elevated)',
    border: '1px solid var(--theme-primary)',
    borderRadius: 12,
    padding: '12px 16px',
    maxWidth: 280,
    fontSize: 14,
    lineHeight: 1.4,
    color: 'var(--theme-text)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
    pointerEvents: 'auto',
  };

  const arrowStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10001,
    width: 0,
    height: 0,
    pointerEvents: 'none',
  };

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  if (placement === 'top') {
    tooltipStyle.left = Math.max(8, Math.min(window.innerWidth - 288, cx - 140));
    tooltipStyle.bottom = window.innerHeight - rect.top + ARROW_SIZE;
    if (config.arrow) {
      arrowStyle.left = cx - ARROW_SIZE;
      arrowStyle.bottom = window.innerHeight - rect.top;
      arrowStyle.borderLeft = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderRight = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderTop = `${ARROW_SIZE}px solid var(--theme-primary)`;
    }
  } else if (placement === 'bottom') {
    tooltipStyle.left = Math.max(8, Math.min(window.innerWidth - 288, cx - 140));
    tooltipStyle.top = rect.bottom + ARROW_SIZE;
    if (config.arrow) {
      arrowStyle.left = cx - ARROW_SIZE;
      arrowStyle.top = rect.bottom;
      arrowStyle.borderLeft = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderRight = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderBottom = `${ARROW_SIZE}px solid var(--theme-primary)`;
    }
  } else if (placement === 'left') {
    tooltipStyle.right = window.innerWidth - rect.left + ARROW_SIZE;
    tooltipStyle.top = Math.max(8, cy - tooltipH / 2);
    if (config.arrow) {
      arrowStyle.right = window.innerWidth - rect.left;
      arrowStyle.top = cy - ARROW_SIZE;
      arrowStyle.borderTop = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderBottom = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderLeft = `${ARROW_SIZE}px solid var(--theme-primary)`;
    }
  } else {
    tooltipStyle.left = rect.right + ARROW_SIZE;
    tooltipStyle.top = Math.max(8, cy - tooltipH / 2);
    if (config.arrow) {
      arrowStyle.left = rect.right;
      arrowStyle.top = cy - ARROW_SIZE;
      arrowStyle.borderTop = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderBottom = `${ARROW_SIZE}px solid transparent`;
      arrowStyle.borderRight = `${ARROW_SIZE}px solid var(--theme-primary)`;
    }
  }

  const isLast = step === 'mc-result' || step === 'finish';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {/* dark backdrop with a hole around target */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <mask id="tour-hole">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={rect.left - 8}
              y={rect.top - 8}
              width={rect.width + 16}
              height={rect.height + 16}
              rx={8}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.5)"
          mask="url(#tour-hole)"
          style={{ pointerEvents: 'auto' }}
          onClick={(e) => {
            const x = e.clientX;
            const y = e.clientY;
            if (
              x < rect.left - 8 ||
              x > rect.left + rect.width + 8 ||
              y < rect.top - 8 ||
              y > rect.top + rect.height + 8
            ) {
              endTour();
            }
          }}
        />
      </svg>

      {/* Arrow */}
      {config.arrow && <div style={arrowStyle} />}

      {/* Tooltip */}
      <div ref={tooltipRef} style={tooltipStyle}>
        <div style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 16 }}>👋</span>
          <span>Подсказка</span>
        </div>
        <div style={{ marginBottom: 12 }}>{config.text}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {!isLast && (
            <button
              onClick={endTour}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid var(--theme-border)',
                background: 'transparent',
                color: 'var(--theme-text-secondary)',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Пропустить
            </button>
          )}
          <button
            onClick={isLast ? endTour : nextStep}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--theme-primary)',
              color: 'var(--theme-primary-text, #1a1a2e)',
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {isLast ? 'Понятно!' : 'Далее'}
          </button>
        </div>
      </div>

      {/* pulse ring around target */}
      <div
        style={{
          position: 'fixed',
          left: rect.left - 8,
          top: rect.top - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderRadius: 8,
          border: '2px solid var(--theme-primary)',
          zIndex: 10000,
          pointerEvents: 'none',
          animation: 'tour-pulse 2s infinite',
        }}
      />

      <style>{`
        @keyframes tour-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 200, 255, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 200, 255, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 200, 255, 0); }
        }
      `}</style>
    </div>
  );
}
